<template>
    <div>
        <ts-page-title
            :title="$t('collateralAsset.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('collateralAsset.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <Button type="info" ghost @click="addNew">{{
                    $t('addNew')
                }}</Button>
                <div class="tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('collateralAsset.search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
            >
                <template slot-scope="{ row }" slot="is_active">
                    <Icon
                        v-if="row.is_active"
                        type="ios-checkmark-circle-outline text-success"
                        size="20"
                    />
                    <Icon
                        v-else
                        type="ios-close-circle-outline text-danger"
                        size="20"
                    />
                </template>
                <template slot-scope="{ row }" slot="action">
                    <i-switch
                        v-tooltip="$t('collateralAsset.isActive')"
                        size="small"
                        v-model="row.is_active"
                        :loading="row._switching"
                        @on-change="onChangeIsActive(row)"
                        style="margin-right: 4px"
                    >
                    </i-switch>
                    <a
                        href="#"
                        class="text-primary ml-4"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>
                    <Poptip
                        confirm
                        :title="$t('areYouSure')"
                        @on-ok="onDelete(row)"
                        :transfer="true"
                        :ok-text="$t('yes')"
                        :cancel-text="$t('no')"
                    >
                        <a
                            class="ml-2 text-danger"
                            v-tooltip="$t('delete')"
                            :disabled="row._deleting"
                        >
                            <i
                                class="fas fa-circle-notch fa-spin"
                                v-if="row._deleting"
                            ></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                    </Poptip>
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
            <!-- form-action -->
            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                :title="$t('collateralAsset.pageTitle')"
                width="600"
            >
                <from-action
                    ref="form_action"
                    @cancel="clearEdit"
                    @fetchData="fetchData"
                />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'

export default {
    name: 'collateralAssetIndex',
    components: {
        FromAction
    },
    data () {
        return {
            loading: false,
            showForm: false,
            width: 'width: 300px'
        }
    },
    computed: {
        ...mapState('creditOperation/collateralAsset', ['resources', 'pagination']),
        search: {
            get () {
                return this.$store.state.creditOperation.collateralAsset.search
            },
            set (newValue) {
                this.$store.commit('creditOperation/collateralAsset/SET_SEARCH', newValue)
                this.$store.commit('creditOperation/collateralAsset/RESET_CURRENT_PAGE')
            }
        },
        columns () {
            return [
                {
                    title: this.$t('collateralAsset.assetCode'),
                    key: 'asset_code',
                    className: 'tw-whitespace-nowrap',
                    sortable: true,
                    width: 150
                },
                {
                    title: this.$t('collateralAsset.assetName'),
                    key: 'asset_name',
                    className: 'tw-whitespace-nowrap',
                    sortable: true,
                    minWidth: 200
                },
                {
                    title: this.$t('collateralAsset.description'),
                    key: 'description',
                    className: 'tw-whitespace-nowrap',
                    minWidth: 250
                },
                {
                    title: this.$t('collateralAsset.isActive'),
                    slot: 'is_active',
                    align: 'center',
                    className: 'tw-whitespace-nowrap',
                    width: 120,
                    filters: [
                        {
                            label: 'Active',
                            value: true
                        },
                        {
                            label: 'Inactive',
                            value: false
                        }
                    ],
                    filterMultiple: false,
                    filterRemote (value) {
                        this.fetchData({
                            filter: {
                                is_active: value
                            }
                        })
                    }
                },
                {
                    title: this.$t('actions'),
                    key: 'action',
                    slot: 'action',
                    fixed: 'right',
                    align: 'center',
                    className: 'tw-space-x-2',
                    width: 150
                }
            ]
        }
    },
    methods: {
        fetchData (attributes) {
            this.loading = true
            this.$store
                .dispatch('creditOperation/collateralAsset/fetch', { ...attributes })
                .then(() => {
                    this.loading = false
                })
                .catch(error => {
                    this.loading = false
                    this.notice({ type: 'error', text: error.message })
                })
        },
        onEdit (record) {
            this.$store.commit('creditOperation/collateralAsset/SET_EDIT_DATA', record)
            this.showForm = true
        },
        clearEdit () {
            this.showForm = false
            this.$refs.form_action.clearInput()
            this.$store.commit('creditOperation/collateralAsset/SET_EDIT_DATA', {})
        },
        addNew () {
            this.$store.commit('creditOperation/collateralAsset/SET_EDIT_DATA', {})
            this.showForm = true
            this.$refs.form_action.clearInput()
        },
        async onDelete (record) {
            record._deleting = true
            this.$store
                .dispatch('creditOperation/collateralAsset/destroy', record.asset_id)
                .then(response => {
                    this.fetchData()
                    record._deleting = false
                    this.notice({
                        type: 'success',
                        text: response.message
                    })
                })
                .catch(error => {
                    this.notice({ type: 'error', text: error.message })
                    record._deleting = false
                })
        },
        async onChangeIsActive (record) {
            record._switching = true
            this.$store
                .dispatch('creditOperation/collateralAsset/changeIsActive', {
                    id: record.asset_id,
                    data: { is_active: record.is_active }
                })
                .then(response => {
                    this.fetchData()
                    record._switching = false
                    this.notice({
                        type: 'success',
                        text: response.message
                    })
                })
                .catch(error => {
                    this.notice({ type: 'error', text: error.message })
                    record._switching = false
                })
        },
        notice (not) {
            this.$Notice[not.type]({
                title: 'COLLATERAL ASSET',
                desc: not.text
            })
        }
    },
    watch: {
        search: debounce(function (search) {
            this.fetchData({ search })
        }, 500)
    },
    beforeRouteLeave (to, from, next) {
        this.$store.commit('creditOperation/collateralAsset/RESET_STATE')
        next()
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.fetchData()
        })
    }
}
</script>
